import { LitElement, html } from 'lit';
import styles from './studio-alert.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioAlert extends LitElement {
  static styles = styles;

  static properties = {
    state: { type: String, reflect: true },
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    title: { type: String, reflect: true },
    description: { type: String, reflect: true },
    primaryButtonText: { type: String, reflect: true, attribute: 'primary-button-text' },
    secondaryButtonText: { type: String, reflect: true, attribute: 'secondary-button-text' },
    icon: { type: String, reflect: true },
    disabled: { type: Boolean, reflect: true },
  };

  static validStates = ['default', 'success', 'info', 'warning', 'error'];
  static validStyleTypes = ['outline', 'filled'];

  constructor() {
    super();
    this.state = 'default';
    this.styleType = 'outline';
    this.title = '';
    this.description = '';
    this.primaryButtonText = 'Action';
    this.secondaryButtonText = 'Dismiss';
    this.icon = '';
    this.disabled = false;
    this._eventListeners = [];
  }

  set state(value) {
    this._state = StudioAlert.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set styleType(value) {
    this._styleType = StudioAlert.validStyleTypes.includes(value) ? value : 'outline';
  }

  get styleType() {
    return this._styleType;
  }

  set title(value) {
    this._title = typeof value === 'string' ? value : '';
  }

  get title() {
    return this._title;
  }

  set description(value) {
    this._description = typeof value === 'string' ? value : '';
  }

  get description() {
    return this._description;
  }

  set primaryButtonText(value) {
    this._primaryButtonText = typeof value === 'string' ? value : 'Action';
  }

  get primaryButtonText() {
    return this._primaryButtonText;
  }

  set secondaryButtonText(value) {
    this._secondaryButtonText = typeof value === 'string' ? value : 'Dismiss';
  }

  get secondaryButtonText() {
    return this._secondaryButtonText;
  }

  set icon(value) {
    this._icon = typeof value === 'string' ? value : '';
  }

  get icon() {
    return this._icon;
  }

  get truncatedTitle() {
    return this._title.length > 40 ? this._title.slice(0, 40) + '...' : this._title;
  }

  get truncatedDescription() {
    return this._description.length > 50 ? this._description.slice(0, 50) + '...' : this._description;
  }

  get truncatedPrimaryButtonText() {
    return this._primaryButtonText.length > 15 ? this._primaryButtonText.slice(0, 15) + '...' : this._primaryButtonText;
  }

  get truncatedSecondaryButtonText() {
    return this._secondaryButtonText.length > 15 ? this._secondaryButtonText.slice(0, 15) + '...' : this._secondaryButtonText;
  }

  firstUpdated() {
    const primaryButton = this.renderRoot.querySelector('.primary');
    const secondaryButton = this.renderRoot.querySelector('.secondary');
    if (!primaryButton || !secondaryButton) return;

    const setState = (button, state, add = true) => {
      if (!this.disabled) {
        button.classList[add ? 'add' : 'remove'](state);
      }
    };

    this._eventListeners = [
      {
        element: primaryButton,
        event: 'click',
        handler: () => this._handlePrimaryClick(),
      },
      {
        element: secondaryButton,
        event: 'click',
        handler: () => this._handleSecondaryClick(),
      },
      {
        element: primaryButton,
        event: 'mouseenter',
        handler: () => setState(primaryButton, 'hover'),
      },
      {
        element: primaryButton,
        event: 'mouseleave',
        handler: () => setState(primaryButton, 'hover', false),
      },
      {
        element: primaryButton,
        event: 'focusin',
        handler: () => setState(primaryButton, 'focus'),
      },
      {
        element: primaryButton,
        event: 'focusout',
        handler: () => setState(primaryButton, 'focus', false),
      },
      {
        element: primaryButton,
        event: 'mousedown',
        handler: () => setState(primaryButton, 'press'),
      },
      {
        element: primaryButton,
        event: 'mouseup',
        handler: () => setState(primaryButton, 'focus'),
      },
      {
        element: primaryButton,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this._handlePrimaryClick();
          }
        },
      },
      {
        element: secondaryButton,
        event: 'mouseenter',
        handler: () => setState(secondaryButton, 'hover'),
      },
      {
        element: secondaryButton,
        event: 'focusin',
        handler: () => setState(secondaryButton, 'focus'),
      },
      {
        element: secondaryButton,
        event: 'focusout',
        handler: () => setState(secondaryButton, 'focus', false),
      },
      {
        element: secondaryButton,
        event: 'mousedown',
        handler: () => setState(secondaryButton, 'press'),
      },
      {
        element: secondaryButton,
        event: 'mouseup',
        handler: () => setState(secondaryButton, 'focus'),
      },
      {
        element: secondaryButton,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this._handleSecondaryClick();
          }
        },
      },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  render() {
    return html`
      <div
        class="alert"
        role="alert"
        aria-labelledby="${this.title ? 'alert-title' : ''}"
        aria-describedby="${this.description ? 'alert-description' : ''}"
        aria-disabled="${this.disabled}"
      >
        <slot name="icon">
          ${this.icon
            ? html`<studio-icon name="${this.icon}" class="icon"></studio-icon>`
            : ''}
        </slot>
        <div class="content">
          <div class="text">
            <slot name="title">
              ${this.title
                ? html`<span class="title" id="alert-title">${this.truncatedTitle}</span>`
                : ''}
            </slot>
            <slot name="description">
              ${this.description
                ? html`<span class="description" id="alert-description">${this.truncatedDescription}</span>`
                : ''}
            </slot>
          </div>
          <div class="buttons">
            <slot name="primary-button">
              ${this.primaryButtonText
                ? html`
                    <button class="primary" ?disabled="${this.disabled}" @click="${this._handlePrimaryClick}">
                      ${this.truncatedPrimaryButtonText}
                    </button>
                  `
                : ''}
            </slot>
            <slot name="secondary-button">
              ${this.secondaryButtonText
                ? html`
                    <button class="secondary" ?disabled="${this.disabled}" @click="${this._handleSecondaryClick}">
                      ${this.truncatedSecondaryButtonText}
                    </button>
                  `
                : ''}
            </slot>
          </div>
        </div>
        <slot></slot>
      </div>
    `;
  }

  _handlePrimaryClick() {
    if (!this.disabled) {
      this.dispatchEvent(new CustomEvent('primary-click', { bubbles: true, composed: true }));
    }
  }

  _handleSecondaryClick() {
    if (!this.disabled) {
      this.dispatchEvent(new CustomEvent('secondary-click', { bubbles: true, composed: true }));
    }
  }
}

customElements.define('studio-alert', StudioAlert);